import { useTranslation } from 'react-i18next';
import { ChevronDown, Sparkles, Image as ImageIcon } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/v2/ui';
import { AIFieldMenuGroup } from './AIFieldMenuGroup';
import { useAIFieldMenuData } from '@/hooks/useAIFieldMenuData';

export interface AIFieldRunArgs {
  templateId?: string;
  presetId?: string;
  imageKey?: string;
  systemToolCode?: string;
}

export interface AIFieldMenuProps {
  productId: string;
  fieldType: string;
  disabled: boolean;
  onRun: (args: AIFieldRunArgs) => void;
}

/** ▾ override menu: own templates + optional product-image (vision) source. */
export function AIFieldMenu({ productId, fieldType, disabled, onRun }: AIFieldMenuProps) {
  const { t } = useTranslation();
  const { templates, tools, vision, imageKey } = useAIFieldMenuData(fieldType, productId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-l-none border-l-0 px-2" disabled={disabled}>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuItem onClick={() => onRun({})}>
          <Sparkles className="h-4 w-4 mr-2" />
          {t('aiField.standard')}
        </DropdownMenuItem>
        {vision && (
          <DropdownMenuItem onClick={() => onRun({ imageKey })}>
            <ImageIcon className="h-4 w-4 mr-2" />
            {t('aiField.withImage')}
          </DropdownMenuItem>
        )}
        <AIFieldMenuGroup
          label={t('aiField.systemTemplates')}
          items={tools.map((tl) => ({ id: tl.code, name: tl.name }))}
          onPick={(code) => onRun({ systemToolCode: code })}
        />
        <AIFieldMenuGroup
          label={t('aiField.myTemplates')}
          items={templates}
          onPick={(id) => onRun({ templateId: id })}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
